import { AlertColor, Stack } from "@mui/material";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ampli, EAmpliUserRole } from "../../../../../ampli";
import { CONTRACT_STATUS_AMPLI } from "../../../../../pages/contract/constants";
import {
  IContractDetailsResponseData,
  IPostFeedBackRequest,
} from "../../../../../setup/api/services/contract/models";
import { postFeedBack } from "../../../../../setup/api/services/feedbackAndReview";
import { getBasicUserDetailsById } from "../../../../../setup/api/services/user";
import { IUserInfoResponse } from "../../../../../setup/api/services/user/models";
import { DialogModal } from "../../../../common/dialog";
import PrimaryButton from "../../../../common/PrimaryButton";
import CustomizedSnackbars from "../../../../customizedSnackbars";
import TextArea from "../../../../forms/textarea";
import { RatingIcon } from "../../../../rating/ratingIcon";
import styles from "./feedBackAndReview.module.scss";

interface IFeedbackAndReviewModal {
  contract: IContractDetailsResponseData;
  modalOpen?: boolean;
  setIsViewButtonFeedback?: () => void;
  closeFeedbackModal: () => void;
  refreshContractDetails: () => void;
}
export const FeedbackAndReviewModal = (props: IFeedbackAndReviewModal) => {
  const { t } = useTranslation();
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>();
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");
  const [starRating, setStarRating] = useState<number>(0);

  const [desc, setDesc] = useState<string>("");
  const {
    contract,
    modalOpen,
    setIsViewButtonFeedback,
    closeFeedbackModal,
    refreshContractDetails,
  } = props;

  const closeModal = () => {
    setSnackBarOpen(false);
    setSnackBarColor(undefined);
    setSnackBarTitle("");
    ClearAfterSubmit();
    closeFeedbackModal();
  };

  const ClearAfterSubmit = () => {
    setStarRating(0);
    setDesc("");
  };

  const changeRating = (newRating) => {
    setStarRating(newRating);
  };

  const sendAmpliEventFeedback = () => {
    ampli.sentFeedback({
      contractId: contract.shortId,
      contractStatus: CONTRACT_STATUS_AMPLI[contract.status],
      ratingScore: starRating,
      userRole: EAmpliUserRole.buyer,
    });
  };
  const fetchUserBasicInfo = async (userId: string) => {
    return await getBasicUserDetailsById(userId);
  };
  const sendAmpliUserPropertiesFeedback = async (
    userBasicInfo: IUserInfoResponse
  ) => {
    ampli.client.setUserProperties({
      userId: userBasicInfo.data.id,
      overallFeedbackScore:
        userBasicInfo.data.profile.feedbackOverallRatingScore.toFixed(2),
    });
  };
  const sendAmpliFeedback = async () => {
    sendAmpliEventFeedback();
    const userBasicInfo = await fetchUserBasicInfo(contract.senderId);
    sendAmpliUserPropertiesFeedback(userBasicInfo);
  };
  const submitData = () => {
    if (starRating !== 0) {
      const payload: IPostFeedBackRequest = {
        contractId: contract.id,
        shortContractId: contract.shortId,
        contractTitle: contract.itemDetail.title,
        receiverId: contract.senderId,
        providerId: contract.receiverId,
        providerRole: "CUSTOMER",
        ratingScore: starRating,
        comment: desc,
      };
      postFeedBack(payload)
        .then(async () => {
          sendAmpliFeedback();
          setSnackBarOpen(true);
          setSnackBarTitle(t("lbl_contract_review_and_feedback_api_msg"));
          setIsViewButtonFeedback && setIsViewButtonFeedback();
          closeModal();
          refreshContractDetails();
        })
        .catch((error) => {
          console.error;
          Sentry.captureException(error);
          setSnackBarOpen(true);
          setSnackBarColor("error");
          setSnackBarTitle(t("lbl_contract_review_and_feedback_api_msg"));
        });
    }
  };

  const renderModal = () => {
    return (
      <DialogModal
        className={styles["review_model"]}
        title={t("lbl_contract_review_and_feedback")}
        open={Boolean(modalOpen)}
        handleClose={() => {
          closeModal();
        }}>
        <div>
          <div className={classNames(styles["desc_container"], styles["mt8"])}>
            <p className={classNames(styles["p1"], styles["g700"])}>
              {t("lbl_contract_review_and_feedback_subtitle")}
            </p>
          </div>
          <div className={styles["rating_section"]}>
            <RatingIcon
              initialValue={0}
              onClick={changeRating}
              showTooltip
              tooltipDefaultText="( 0 )"
              tooltipArray={[
                "( 0.5 )",
                "( 1 )",
                "( 1.5 )",
                "( 2 )",
                "( 2.5 )",
                "( 3 )",
                "( 3.5 )",
                "( 4 )",
                "( 4.5 )",
                "( 5 )",
              ]}
              tooltipClassName={classNames(
                styles["hint"],
                styles["g200"],
                styles["tooltips_feedback"]
              )}
              size={28.5}
            />
          </div>
          <p
            className={classNames(
              styles["subtitle2"],
              styles["mb2"],
              styles["desc_container"]
            )}>
            {t("lbl_contract_review_and_feedback_description")}
          </p>
          <div className={styles.mb16}>
            <TextArea
              label={t(
                "lbl_contract_review_and_feedback_description_placeholder"
              )}
              height={"100px"}
              required={false}
              defaultValue={desc}
              maxCharacters={100}
              handleChange={(e) => setDesc(e.target.value)}
            />
          </div>

          <PrimaryButton
            id={`contract_details_submit_feedback`}
            onClick={submitData}
            disabled={starRating === 0}>
            {t("lbl_contract_review_and_feedback_submit")}
          </PrimaryButton>
        </div>
      </DialogModal>
    );
  };

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  return (
    <div>
      <Stack spacing={2} justifyContent="center" width={"100%"}>
        {renderModal()}
        {renderSnackBar()}
      </Stack>
    </div>
  );
};
